/**
 * Import feature module (W2-09 publish-import; DESIGN.md §5.12, §4.17; WORKPACKAGES Appendix B: `file.import`,
 * `file.importFolder`, `file.importSummary`, `file.cancelImport` of the `file.*` namespace).
 *
 * Provides:
 *  - THE global file-drop handlers (Library and editor; the shell asks them by priority): charts, packages and
 *    folders first, then anything else (a song or picture on its own starts the new chart wizard);
 *  - commands: `file.import` (Ctrl+O, the file picker), `file.importFolder` (the folder picker), `file.importSummary`
 *    and `file.cancelImport`;
 *  - the import report dialog (`DIALOG.importReport`) and the skin install dialog (`DIALOG.skinInstall`);
 *  - the import queue banner (status item, slot 'banner': Library progress) and its dialogs (overlay: summary,
 *    batch-export picker);
 *  - the Settings row of the merge placement and a help topic.
 */

import { useCallback, useSyncExternalStore } from 'react';
import { DIALOG } from '../../app/dialogs/types';
import type {
  AppServices,
  CommandDef,
  FeatureModule,
  FileDropHandler,
  HelpTopicDef,
  KeyBinding,
  KeymapPresetId,
  SettingDef,
} from '../../app/registry/types';
import { Select } from '../../ui/Select';
import { ImportBanner, ImportQueueDialogs } from './ImportBanner';
import { useText } from './hooks';
import { m } from './import.messages';
import { looksLikeCharts, pickFiles, PICK_ACCEPT } from './importFlow';
import { importQueue } from './importQueue';
import { ImportReportDialog } from './ImportReportDialog';
import { MERGE_ALIGNS, readMergeAlign, writeMergeAlign, type MergeAlign } from './mergeFlow';
import { SkinInstallDialog } from './SkinInstallDialog';

/** Replaceable browser pickers (tests). */
export const importDeps: {
  pick: (opts: { readonly directory: boolean; readonly accept?: string }) => Promise<File[]>;
} = {
  pick: pickFiles,
};

async function pickAndQueue(services: AppServices, directory: boolean): Promise<void> {
  const files = await importDeps.pick(directory ? { directory } : { directory, accept: PICK_ACCEPT });
  if (files.length > 0) importQueue.enqueue(services, files);
}

const IMPORT_COMMANDS: readonly CommandDef[] = [
  {
    id: 'file.import',
    title: m.cmdImport,
    category: 'File',
    icon: 'FolderOpen',
    level: 'guided',
    hint: 3,
    aliases: ['open', 'import', 'load', 'pez', 'zip', 'rpe', 'pec', 'chart file', '导入', '打开谱面'],
    menus: [{ menu: 'app.project', group: 'file', order: 10 }],
    async run(ctx) {
      await pickAndQueue(ctx, false);
    },
  },
  {
    id: 'file.importFolder',
    title: m.cmdImportFolder,
    category: 'File',
    icon: 'FolderInput',
    level: 'guided',
    aliases: ['open folder', 'import folder', 'resources', 'RPE Resources', '导入文件夹'],
    menus: [{ menu: 'app.project', group: 'file', order: 12 }],
    async run(ctx) {
      await pickAndQueue(ctx, true);
    },
  },
  {
    id: 'file.importSummary',
    title: m.cmdImportSummary,
    category: 'File',
    icon: 'ListChecks',
    level: 'guided',
    aliases: ['import report', 'import log'],
    enabled: () => (importQueue.snapshot().outcomes.length > 0 ? true : { key: m.reasonNoSummary }),
    run() {
      importQueue.openSummary();
    },
  },
  {
    id: 'file.cancelImport',
    title: m.cmdCancelImport,
    category: 'File',
    icon: 'CircleSlash',
    level: 'guided',
    enabled: () => (importQueue.snapshot().running ? true : { key: m.reasonNotRunning }),
    run(ctx) {
      importQueue.cancel(ctx);
    },
  },
];

const OPEN_KEY: KeyBinding = { chord: 'Ctrl+KeyO', command: 'file.import' };

export const IMPORT_KEYBINDINGS: Readonly<Record<KeymapPresetId, readonly KeyBinding[]>> = {
  default: [OPEN_KEY],
  rpe: [OPEN_KEY],
};

/** The global drop handlers: charts, packages and folders first; anything else (media) after them. */
export function importDropHandlers(services: AppServices): readonly FileDropHandler[] {
  const handle = (files: readonly File[]): Promise<void> => {
    importQueue.enqueue(services, files);
    return Promise.resolve();
  };
  return [
    { id: 'import.charts', priority: 100, accept: (files) => looksLikeCharts(files), handle },
    { id: 'import.media', priority: 10, accept: (files) => files.length > 0, handle },
  ];
}

const ALIGN_TITLES: Readonly<Record<MergeAlign, string>> = {
  auto: m.alignAuto,
  beats: m.alignBeats,
  musicTime: m.alignMusic,
};

function MergeAlignSetting(props: { readonly services: AppServices }) {
  const { services } = props;
  const text = useText(services);
  const subscribe = useCallback(
    (cb: () => void) =>
      services.settings.subscribe(() => {
        cb();
      }),
    [services],
  );
  const read = useCallback(() => readMergeAlign(services.settings), [services]);
  const value = useSyncExternalStore(subscribe, read, read);
  return (
    <Select<MergeAlign>
      ariaLabel={text.t(m.settingAlign)}
      value={value}
      options={MERGE_ALIGNS.map((a) => ({ value: a, label: text.t(ALIGN_TITLES[a]) }))}
      onChange={(v) => {
        writeMergeAlign(services.settings, v);
      }}
    />
  );
}

const SETTINGS: readonly SettingDef[] = [
  {
    path: 'ext.import.mergeAlign',
    title: m.settingAlign,
    level: 'full',
    kind: 'custom',
    component: MergeAlignSetting,
  },
];

const HELP: readonly HelpTopicDef[] = [{ id: 'import', title: m.helpTitle, body: m.helpBody, level: 'guided' }];

export default {
  id: 'import',
  commands: IMPORT_COMMANDS,
  keybindings: IMPORT_KEYBINDINGS,
  dialogs: [
    { id: DIALOG.importReport, kind: 'modal', component: ImportReportDialog },
    { id: DIALOG.skinInstall, kind: 'modal', component: SkinInstallDialog },
  ],
  fileDropHandlers: importDropHandlers,
  statusItems: [{ id: 'import.queue', slot: 'banner', order: 5, component: ImportBanner }],
  overlays: [{ id: 'import.queueDialogs', component: ImportQueueDialogs }],
  settings: SETTINGS,
  helpTopics: HELP,
} satisfies FeatureModule;
